import Autorenew from "@mui/icons-material/Autorenew";
import Cancel from "@mui/icons-material/Cancel";
import Check from "@mui/icons-material/Check";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import PersonAddAlt from "@mui/icons-material/PersonAddAlt";
import Save from "@mui/icons-material/Save";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  GridActionsCellItem,
  GridCellModes,
  GridCellModesModel,
  GridColDef,
  GridRowId,
  GridSortModel,
} from "@mui/x-data-grid";
import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { getInitials } from "../components/Account/utils";
import PageContainer from "../components/Common/PageContainer";
import SectionTable, { NoRowsOverlay } from "../components/Common/SectionTable";
import DashboardLayout from "../components/DashboardLayout/DashboardLayout";
import InternalError from "../components/Errors/InternalError";
import Loading from "../components/Loading";
import WorkspacePageHeader from "../components/PageHeader/WorkspacePage";
import {
  useUpdateWorkspaceUser,
  useWorkspaces,
  useWorkspaceUsers,
} from "../components/Rbac/RbacQueries";
import DeleteUserDialog from "../components/useDialogs/DeleteUserDialog";
import { useDialogs } from "../components/useDialogs/useDialogs";
import useSession from "../components/useSession/useSession";
import { RoleName, UserWorkspaceRole } from "../interfaces/rbac";

export const WorkspacePage: React.FC = () => {
  const dialogs = useDialogs();
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [sortFields, setsortFields] = useState<GridSortModel | undefined>([
    { field: "first_name", sort: "asc" },
    { field: "last_name", sort: "asc" },
  ]);
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const [workspaceId, setWorkspaceId] = useState<string>("");
  const [workspaceIdx, setWorkspaceIdx] = useState<number>(0);
  const session = useSession();
  const {
    data: workspaces,
    isLoading: isWorkspacesLoading,
    isError: isWorkspacesError,
  } = useWorkspaces();
  const {
    data: users,
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = useWorkspaceUsers(workspaceId, {
    page: pageNumber,
    limit: pageSize,
    sortFields: sortFields,
  });
  const { mutateAsync: updateWorkspaceUser } = useUpdateWorkspaceUser();
  const isManager = session?.ManagerGroups.includes(workspaceId) ?? false;
  const isAdmin = session?.isAdmin ?? false;

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      setsortFields(
        sortModel.flatMap((model) => {
          if (model.field === "user") {
            return [
              {
                field: "first_name",
                sort: model.sort,
              },
              {
                field: "last_name",
                sort: model.sort,
              },
            ];
          } else if (model.field === "timestamp") {
            return [
              {
                field: "workspace_added_at",
                sort: model.sort,
              },
            ];
          } else {
            return [
              {
                field: model.field,
                sort: model.sort,
              },
            ];
          }
        })
      );
    },
    []
  );

  useEffect(() => {
    if (_.keys(session?.roles?.workspaces).length > 0) {
      setWorkspaceId(_.keys(session!.roles!.workspaces)[workspaceIdx]);
    }
  }, [session, workspaceIdx]);

  const handleDeleteClick = (id: GridRowId) => () => {
    dialogs.open(DeleteUserDialog, {
      id,
      deleteUser: (id) => {
        updateWorkspaceUser({
          userId: id as string,
          workspaceId: workspaceId,
          role: null,
        });
      },
    });
  };

  const handleEditClick = (id: GridRowId) => () => {
    setCellModesModel({
      ...cellModesModel,
      [id]: {
        workspace_role: { mode: GridCellModes.Edit },
      },
    });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setCellModesModel({
      ...cellModesModel,
      [id]: { workspace_role: { mode: GridCellModes.View } },
    });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setCellModesModel({
      ...cellModesModel,
      [id]: {
        workspace_role: { mode: GridCellModes.View, ignoreModifications: true },
      },
    });
  };

  function getRowId(user: UserWorkspaceRole) {
    return user.user_id;
  }
  const columns: GridColDef<UserWorkspaceRole>[] = [
    {
      field: "user",
      headerName: "Utilisateur",
      flex: 1,
      minWidth: 300,
      editable: isManager,
      renderCell: (params) => (
        <Stack direction="row" spacing={2}>
          <Avatar>
            {getInitials({
              first_name: params.row.first_name,
              last_name: params.row.last_name,
            })}
          </Avatar>
          <Stack>
            <Typography variant="body1" className="font-semibold">
              {params.row.first_name} {params.row.last_name}
            </Typography>
            <Typography variant="body2" className="text-gray-500">
              {params.row.email}
            </Typography>
          </Stack>
        </Stack>
      ),
      cellClassName: "flex items-center",
    },
    {
      field: "workspace_role",
      headerName: "Role",
      flex: 1,
      maxWidth: 150,
      cellClassName: "flex items-center",
      editable: true,
      type: "singleSelect",
      valueOptions: Object.values(RoleName),
      disableColumnMenu: true,
    },
    {
      field: "timestamp",
      headerName: "Activité",
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack spacing={0.5}>
          <Tooltip title="Utilisateur créé le" leaveDelay={100}>
            <div className="flex items-center gap-2">
              <PersonAddAlt />
              <Typography variant="body2">
                {new Date(params.row.user_created_at).toLocaleDateString()}
              </Typography>
            </div>
          </Tooltip>
          <Tooltip
            title="Utilisateur ajouté à l'espace de travail le"
            leaveDelay={100}
          >
            <div className="flex items-center gap-2">
              <Check />
              <Typography variant="body2">
                {new Date(params.row.workspace_added_at).toLocaleDateString()}
              </Typography>
            </div>
          </Tooltip>
          <Tooltip title="Dernière activité de l'utilisateur" leaveDelay={100}>
            <div className="flex items-center gap-2">
              <Autorenew />
              <Typography variant="body2">
                {params.row.user_last_activity
                  ? new Date(params.row.user_last_activity).toLocaleDateString()
                  : "-"}
              </Typography>
            </div>
          </Tooltip>
        </Stack>
      ),
      cellClassName: "flex items-center",
    },
    ...(isManager
      ? [
          {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 100,
            cellClassName: "actions",
            getActions: ({ id }) => {
              const isInEditMode =
                cellModesModel[id]?.workspace_role?.mode === GridCellModes.Edit;
              if (isInEditMode) {
                return [
                  <GridActionsCellItem
                    icon={<Save />}
                    label="Enregistrer"
                    sx={{
                      color: "primary.main",
                    }}
                    onClick={handleSaveClick(id)}
                  />,
                  <GridActionsCellItem
                    icon={<Cancel />}
                    label="Annuler"
                    className="textPrimary"
                    onClick={handleCancelClick(id)}
                    color="inherit"
                  />,
                ];
              }
              return [
                <GridActionsCellItem
                  icon={<Edit />}
                  label="Modifier"
                  onClick={handleEditClick(id)}
                />,
                <GridActionsCellItem
                  icon={<Delete />}
                  label="Supprimer"
                  onClick={handleDeleteClick(id)}
                />,
              ];
            },
          } as GridColDef<UserWorkspaceRole>,
        ]
      : []),
  ];

  const HeaderComponent = useCallback(
    () => (
      <WorkspacePageHeader
        isAdmin={isAdmin}
        isManager={isManager}
        workspaceId={workspaceId}
        workspaceName={
          workspaces?.find((workspace) => workspace._id === workspaceId)
            ?.name ?? ""
        }
        workspaceUsers={users?.items.map((user) => user.user_id) ?? []}
      />
    ),
    [isManager, isAdmin, workspaceId, users, workspaces]
  );

  return (
    <DashboardLayout>
      <PageContainer slots={{ header: HeaderComponent }} className="max-w-full">
        {isUsersError || isWorkspacesError ? (
          <InternalError />
        ) : isUsersLoading || isWorkspacesLoading ? (
          <Loading />
        ) : (
          <Stack direction="row" spacing={2}>
            <Tabs
              orientation="vertical"
              value={workspaceIdx}
              onChange={(event, idx) => {
                setWorkspaceIdx(idx);
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {_.keys(session?.roles?.workspaces).map((workspaceId, idx) => (
                <Tab
                  key={idx}
                  label={
                    workspaces?.find(
                      (workspace) => workspace._id === workspaceId
                    )?.name
                  }
                />
              ))}
            </Tabs>
            <SectionTable<UserWorkspaceRole>
              rows={users?.items ?? []}
              sx={{
                "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
                  border: "none",
                  borderBottom: "none",
                },
                "& .MuiDataGrid-row:hover": {
                  cursor: "default",
                  backgroundColor: "inherit",
                },
              }}
              dataGridProps={{
                slots: {
                  noRowsOverlay: () =>
                    NoRowsOverlay(
                      "Aucun utilisateur dans cet espace de travail"
                    ),
                },
                getRowId: getRowId,
                columns,
                rowHeight: 100,
                editMode: "cell",
                cellModesModel,
                processRowUpdate: async (newRow, oldRow) => {
                  try {
                    await updateWorkspaceUser({
                      userId: newRow.user_id,
                      workspaceId,
                      role:
                        newRow._action === "delete"
                          ? null
                          : newRow.workspace_role,
                    });
                    return newRow;
                  } catch (error) {
                    console.error("Mutation error:", error);
                    throw error;
                  }
                },
                onProcessRowUpdateError: (error) => {
                  console.error("Row update error:", error);
                },
              }}
              paginationProps={
                users && {
                  totalRows: users.total_count,
                  pageNumber,
                  setPageNumber,
                  pageSize,
                  setPageSize,
                  handleSortModelChange: handleSortModelChange,
                }
              }
            />
          </Stack>
        )}
      </PageContainer>
    </DashboardLayout>
  );
};
